import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import { ResetApiState } from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import {
  CancellationRequest,
  CancellationRequestStateTypes,
} from "./CancellationRequest.util";

const name = "refunds";
const extraActions = createExtraActions();

const CancellationRequestInitialState: CancellationRequestStateTypes = {
  approveCancellationRequest: ResetApiState(null),
  resetApproveCancellationRequest: ResetApiState(null),

  rejectCancellationRequest: ResetApiState(null),
  resetRejectCancellationRequest: ResetApiState(null),

  revertCancellationRequest: ResetApiState(null),
  revertAuthorizeCancellationRequest: ResetApiState(null),

  resetRevertCancellationRequest: ResetApiState(null),

  cancellationRequests: ResetApiState([]),

  resetStates: ResetApiState(null),

  authorizeCancellationRequest: ResetApiState(null),
  resetAuthorizeCancellationRequest: ResetApiState(null),

  rejectAuthorizeCancellationRequest: ResetApiState(null),
  resetRejectAuthorizeCancellationRequest: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const approveCancellationRequest = createAsyncThunk<CancellationRequest, any>(
    `/approve-check-${name}`,
    async ({ cancellationPolicyId, id, excludeCargo }: any, thunkApi) =>
      axios
        .put(`/${name}/accept-check/${id}`, {
          cancellationPolicyId,
          excludeCargo,
        })
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetApproveCancellationRequest = createAction(
    `/reset-approve-${name}`
  );
  const resetRevertAuthorizeCancellationRequest = createAction(
    `/reset-revert-authorize-${name}`
  );

  const rejectCancellationRequest = createAsyncThunk<CancellationRequest, any>(
    `/reject-check-${name}`,
    async ({ id, reason }: any, thunkApi) =>
      axios
        .put(`/${name}/reject-check/${id}`, { reason })
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetRejectCancellationRequest = createAction(`/reset-reject-${name}`);

  const revertCancellationRequest = createAsyncThunk<CancellationRequest, any>(
    `/revert-${name}`,
    async (id: any, thunkApi) =>
      axios
        .put(`/${name}/revert/${id}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );
  const revertAuthorizeCancellationRequest = createAsyncThunk<
    CancellationRequest,
    any
  >(`/revert-authorize-${name}`, async (id: any, thunkApi) =>
    axios
      .put(`/${name}/revert_authorize/${id}`)
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetRevertCancellationRequest = createAction(`/reset-revert-${name}`);

  const getCancellationRequests = createAsyncThunk<CancellationRequest[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const authorizeCancellationRequest = createAsyncThunk<
    CancellationRequest,
    any
  >(`/authorize-${name}`, async (id: any, thunkApi) =>
    axios
      .put(`/${name}/accept-approve/${id}`)
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetAuthorizeCancellationRequest = createAction(
    `/reset-authorize-${name}`
  );

  const rejectAuthorizeCancellationRequest = createAsyncThunk<
    CancellationRequest,
    any
  >(`/reject-authorize-${name}`, async (id: any, thunkApi) =>
    axios
      .put(`/${name}/reject-approve/${id}`)
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetRejectAuthorizeCancellationRequest = createAction(
    `/reset-reject-authorize-${name}`
  );

  const resetStates = createAction(`/reset-states`);

  return {
    approveCancellationRequest,
    resetApproveCancellationRequest,
    rejectCancellationRequest,
    resetRejectCancellationRequest,
    revertCancellationRequest,
    revertAuthorizeCancellationRequest,
    resetRevertCancellationRequest,
    resetRevertAuthorizeCancellationRequest,
    getCancellationRequests,
    authorizeCancellationRequest,
    resetAuthorizeCancellationRequest,
    rejectAuthorizeCancellationRequest,
    resetRejectAuthorizeCancellationRequest,

    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<CancellationRequestStateTypes>
) {
  return {
    ...approveCancellationRequestReducer(),
    ...resetApproveCancellationRequest(),
    ...rejectCancellationRequestReducer(),
    ...resetRejectCancellationRequest(),
    ...revertCancellationRequestReducer(),
    ...revertAuthorizeCancellationRequestReducer(),
    ...resetRevertAuthorizeCancellationRequestReducer(),
    ...resetRevertCancellationRequest(),
    ...authorizeCancellationRequestReducer(),
    ...resetAuthorizeCancellationRequest(),
    ...rejectAuthorizeCancellationRequestReducer(),
    ...resetRejectAuthorizeCancellationRequest(),
    ...cancellationRequestsReducer(),
    ...resetStatesReducer(),
  };

  function approveCancellationRequestReducer() {
    return {
      ...builder.addCase(
        extraActions.approveCancellationRequest.pending,
        (state, action) => {
          state.approveCancellationRequest = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.approveCancellationRequest.fulfilled,
        (state, action) => {
          state.approveCancellationRequest = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.approveCancellationRequest.rejected,
        (state, action) => {
          state.approveCancellationRequest = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetApproveCancellationRequest() {
    return {
      ...builder.addCase(
        extraActions.resetApproveCancellationRequest,
        (state, action) => {
          state.resetApproveCancellationRequest = ResetApiState(true);
          state.approveCancellationRequest = ResetApiState(null);
        }
      ),
    };
  }

  function rejectCancellationRequestReducer() {
    return {
      ...builder.addCase(
        extraActions.rejectCancellationRequest.pending,
        (state, action) => {
          state.rejectCancellationRequest = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.rejectCancellationRequest.fulfilled,
        (state, action) => {
          state.rejectCancellationRequest = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.rejectCancellationRequest.rejected,
        (state, action) => {
          state.rejectCancellationRequest = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetRejectCancellationRequest() {
    return {
      ...builder.addCase(
        extraActions.resetRejectCancellationRequest,
        (state, action) => {
          state.resetRejectCancellationRequest = ResetApiState(true);
          state.rejectCancellationRequest = ResetApiState(null);
        }
      ),
    };
  }

  function revertCancellationRequestReducer() {
    return {
      ...builder.addCase(
        extraActions.revertCancellationRequest.pending,
        (state, action) => {
          state.revertCancellationRequest = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.revertCancellationRequest.fulfilled,
        (state, action) => {
          state.revertCancellationRequest = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.revertCancellationRequest.rejected,
        (state, action) => {
          state.revertCancellationRequest = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }
  function revertAuthorizeCancellationRequestReducer() {
    return {
      ...builder.addCase(
        extraActions.revertAuthorizeCancellationRequest.pending,
        (state, action) => {
          state.revertAuthorizeCancellationRequest = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.revertAuthorizeCancellationRequest.fulfilled,
        (state, action) => {
          state.revertAuthorizeCancellationRequest = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.revertAuthorizeCancellationRequest.rejected,
        (state, action) => {
          state.revertAuthorizeCancellationRequest = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }
  function resetRevertCancellationRequest() {
    return {
      ...builder.addCase(
        extraActions.resetRevertCancellationRequest,
        (state, action) => {
          state.resetRevertCancellationRequest = ResetApiState(true);
          state.revertCancellationRequest = ResetApiState(null);
        }
      ),
    };
  }
  function resetRevertAuthorizeCancellationRequestReducer() {
    return {
      ...builder.addCase(
        extraActions.resetRevertAuthorizeCancellationRequest,
        (state, action) => {
          state.revertAuthorizeCancellationRequest = ResetApiState(null);
        }
      ),
    };
  }

  function cancellationRequestsReducer() {
    return {
      ...builder.addCase(
        extraActions.getCancellationRequests.pending,
        (state, action) => {
          state.cancellationRequests = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getCancellationRequests.fulfilled,
        (state, action) => {
          state.cancellationRequests = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getCancellationRequests.rejected,
        (state, action) => {
          state.cancellationRequests = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function authorizeCancellationRequestReducer() {
    return {
      ...builder.addCase(
        extraActions.authorizeCancellationRequest.pending,
        (state, action) => {
          state.authorizeCancellationRequest = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.authorizeCancellationRequest.fulfilled,
        (state, action) => {
          state.authorizeCancellationRequest = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.authorizeCancellationRequest.rejected,
        (state, action) => {
          state.authorizeCancellationRequest = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetAuthorizeCancellationRequest() {
    return {
      ...builder.addCase(
        extraActions.resetAuthorizeCancellationRequest,
        (state, action) => {
          state.resetAuthorizeCancellationRequest = ResetApiState(true);
          state.authorizeCancellationRequest = ResetApiState(null);
        }
      ),
    };
  }

  function rejectAuthorizeCancellationRequestReducer() {
    return {
      ...builder.addCase(
        extraActions.rejectAuthorizeCancellationRequest.pending,
        (state, action) => {
          state.rejectAuthorizeCancellationRequest = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.rejectAuthorizeCancellationRequest.fulfilled,
        (state, action) => {
          state.rejectAuthorizeCancellationRequest = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.rejectAuthorizeCancellationRequest.rejected,
        (state, action) => {
          state.rejectAuthorizeCancellationRequest = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetRejectAuthorizeCancellationRequest() {
    return {
      ...builder.addCase(
        extraActions.resetRejectAuthorizeCancellationRequest,
        (state, action) => {
          state.resetRejectAuthorizeCancellationRequest = ResetApiState(true);
          state.rejectAuthorizeCancellationRequest = ResetApiState(null);
        }
      ),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.approveCancellationRequest = ResetApiState(null);
        state.resetApproveCancellationRequest = ResetApiState(null);
        state.revertAuthorizeCancellationRequest = ResetApiState(null);
        state.rejectCancellationRequest = ResetApiState(null);
        state.resetRejectCancellationRequest = ResetApiState(null);
        state.authorizeCancellationRequest = ResetApiState(null);
        state.resetAuthorizeCancellationRequest = ResetApiState(null);
        state.rejectAuthorizeCancellationRequest = ResetApiState(null);
        state.resetRejectAuthorizeCancellationRequest = ResetApiState(null);
        state.cancellationRequests = ResetApiState([]);
      }),
    };
  }
}

const CancellationRequestSlice = createSlice({
  name,
  initialState: CancellationRequestInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const CancellationRequestActions = {
  ...CancellationRequestSlice.actions,
  ...extraActions,
};
export const CancellationRequestReducer = CancellationRequestSlice.reducer;
